/**
 * F-GTCC-COCKPIT-EXEC1 — typed read-only fetchers for the Monitoring
 * Cockpit's "Executive Summary" tab.
 *
 * Endpoints per the GTCC 1:1 program (bindend, identisch fuer die
 * API-Pakete G/H/I/J/K). Every fetcher maps failures onto the same
 * discriminated union `GtccFetchResult<T>` used across `monitoring/api.ts`
 * so the panels never have to know about `ApiError` / network throws.
 *
 * NEVER mutates anything; only documented GET paths are sent.
 */

import { apiGet, ApiError } from "../lib/api.js";
import type {
  GtccAlertSeverityEnvelopeDto,
  GtccExecSummaryDto,
  GtccLatencySeriesEnvelopeDto,
  GtccRecentChangesEnvelopeDto,
  GtccRowsEnvelopeDto,
  GtccTelemetryIngressEnvelopeDto,
} from "./exec-types.js";

export type GtccFetchResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | {
      readonly ok: false;
      readonly kind: "error";
      readonly httpStatus: number;
      readonly message: string;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

async function fetchGtccJson<T>(path: string, signal?: AbortSignal): Promise<GtccFetchResult<T>> {
  try {
    const data = await apiGet<T>(path, undefined, signal);
    return { ok: true, data };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (err.status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "error", httpStatus: err.status, message: err.message };
    }
    const message = err instanceof Error ? err.message : `${path} fetch failed`;
    return { ok: false, kind: "transport_error", message };
  }
}

export const GTCC_EXEC_SUMMARY_PATH = "/v1/admin/gtcc/exec/summary";
export const GTCC_LATENCY_SERIES_PATH = "/v1/admin/gtcc/series/latency";
export const GTCC_TELEMETRY_INGRESS_PATH = "/v1/admin/gtcc/series/telemetry-ingress";
export const GTCC_ALERT_SEVERITY_PATH = "/v1/admin/gtcc/series/alert-severity";
export const GTCC_VEHICLE_FLOW_PATH = "/v1/admin/gtcc/ops/vehicle-flow";
export const GTCC_BACKUP_READINESS_PATH = "/v1/admin/gtcc/ops/backup-readiness";
export const GTCC_JOBS_PATH = "/v1/admin/gtcc/ops/jobs";
export const GTCC_RECENT_CHANGES_PATH = "/v1/admin/gtcc/ops/recent-changes";
export const GTCC_SYNTHETIC_PATH = "/v1/admin/gtcc/svc/synthetic";
export const GTCC_DELIVERY_WATCHDOG_PATH = "/v1/admin/gtcc/svc/delivery-watchdog";

export function fetchGtccExecSummary(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccExecSummaryDto>> {
  return fetchGtccJson<GtccExecSummaryDto>(GTCC_EXEC_SUMMARY_PATH, signal);
}

export function fetchGtccLatencySeries(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccLatencySeriesEnvelopeDto>> {
  return fetchGtccJson<GtccLatencySeriesEnvelopeDto>(
    `${GTCC_LATENCY_SERIES_PATH}?range=24h`,
    signal,
  );
}

export function fetchGtccTelemetryIngress(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccTelemetryIngressEnvelopeDto>> {
  return fetchGtccJson<GtccTelemetryIngressEnvelopeDto>(
    `${GTCC_TELEMETRY_INGRESS_PATH}?range=24h`,
    signal,
  );
}

export function fetchGtccAlertSeverity(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccAlertSeverityEnvelopeDto>> {
  return fetchGtccJson<GtccAlertSeverityEnvelopeDto>(
    `${GTCC_ALERT_SEVERITY_PATH}?range=7d`,
    signal,
  );
}

export function fetchGtccVehicleFlow(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccRowsEnvelopeDto>> {
  return fetchGtccJson<GtccRowsEnvelopeDto>(GTCC_VEHICLE_FLOW_PATH, signal);
}

export function fetchGtccBackupReadiness(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccRowsEnvelopeDto>> {
  return fetchGtccJson<GtccRowsEnvelopeDto>(GTCC_BACKUP_READINESS_PATH, signal);
}

export function fetchGtccJobs(signal?: AbortSignal): Promise<GtccFetchResult<GtccRowsEnvelopeDto>> {
  return fetchGtccJson<GtccRowsEnvelopeDto>(GTCC_JOBS_PATH, signal);
}

export function fetchGtccRecentChanges(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccRecentChangesEnvelopeDto>> {
  return fetchGtccJson<GtccRecentChangesEnvelopeDto>(GTCC_RECENT_CHANGES_PATH, signal);
}

export function fetchGtccSynthetic(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccRowsEnvelopeDto>> {
  return fetchGtccJson<GtccRowsEnvelopeDto>(GTCC_SYNTHETIC_PATH, signal);
}

export function fetchGtccDeliveryWatchdog(
  signal?: AbortSignal,
): Promise<GtccFetchResult<GtccRowsEnvelopeDto>> {
  return fetchGtccJson<GtccRowsEnvelopeDto>(GTCC_DELIVERY_WATCHDOG_PATH, signal);
}
